import type {PerfilLineaRead, PerfilLineaWrite} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'

/** En el orden del documento (Res. 1843/2025 art. 16 lit. a); espejo de `BLOQUES_DEL_PERFIL` del backend.
 * ALL_CAPS porque la doctrina de formularios (R3) lo lee como enum cerrado. */
const BLOQUES = [
  'funciones',
  'responsabilidades',
  'habilidades',
  'competencias',
  'requerimientos',
  'peligros',
  'medidas_prevencion',
  'condiciones_especiales',
] as const
export type Bloque = (typeof BLOQUES)[number]

/** Bloques que son una tabla «ítem» (KAI-146): el texto de la línea es el ítem. */
export const CON_DESCRIPCION = new Set<Bloque>(['habilidades', 'competencias', 'requerimientos'])

/** Se cargan pegando viñetas (KAI-146). Cada fila es su propia línea, nunca un párrafo: la línea con
 * su origen es lo que leen la carpeta de visita, el catálogo y el contraste con obras. */
export const POR_LOTE = new Set<Bloque>(['funciones', 'habilidades', 'competencias', 'requerimientos'])

/** Escribirla o traerla de una propuesta ya la confirma (KAI-146); el acto legal sigue siendo la firma del perfil. */
export const CONFIRMADAS_AL_ENTRAR = new Set<Bloque>(['funciones'])

/** Espejo de `RequisitoTipo` del backend. */
export const TIPOS_REQUISITO = ['fisico', 'mental', 'tecnico'] as const
export type TipoRequisito = (typeof TIPOS_REQUISITO)[number]

/** Espejo de `CompetenciaTipo` del backend. */
export const TIPOS_COMPETENCIA = ['tecnica', 'comportamental'] as const
export type TipoCompetencia = (typeof TIPOS_COMPETENCIA)[number]

/** Anexo A de la GTC 45, espejo de `RiesgoFilaClasificacion`; los rótulos viven en `ipevr.clasificacion.*`. */
export const CLASES_GTC = [
  'biologico',
  'fisico',
  'quimico',
  'biomecanico',
  'psicosocial',
  'condiciones_de_seguridad',
  'fenomenos_naturales',
] as const

/** Los peligros y sus medidas no pasan por acá: se escriben juntos en el diálogo del riesgo (KAI-334). */
export type ExtrasCrudos = {
  tipo: TipoRequisito
  tipoCompetencia: TipoCompetencia
}
export const EXTRAS_VACIOS: ExtrasCrudos = {
  tipo: 'tecnico',
  tipoCompetencia: 'tecnica',
}

/** Una medida no lleva ninguno: el servidor lo rechaza (KAI-151). */
function subtipoDe(bloque: Bloque, extras: ExtrasCrudos): string | null {
  if (bloque === 'requerimientos') return extras.tipo
  if (bloque === 'competencias') return extras.tipoCompetencia
  return null
}

export function extrasDeLinea(bloque: Bloque, extras: ExtrasCrudos): Pick<PerfilLineaWrite, 'subtipo'> {
  return {subtipo: subtipoDe(bloque, extras)}
}

export type Apertura = {modo: 'nueva'; bloque: Bloque} | {modo: 'editar'; linea: PerfilLineaRead}

/** Espejo exacto de `clave_de_peligro` del servidor: una medida nueva cuelga de esta clave antes de
 * que el peligro tenga `campo:`. */
export function claveDePeligro(clase: string, texto: string): string {
  return `peligro:${clase}:${texto.trim().toLowerCase()}`
}

/** Una línea por renglón, sin viñetas ni numeración tecleadas y sin vacías. */
export function renglonesDe(texto: string): string[] {
  return texto
    .split('\n')
    .map((renglon) =>
      renglon
        .trim()
        .replace(/^(?:[•·\-*]+|\d+[.)])\s*/, '')
        .trim()
    )
    .filter(Boolean)
}

/** Peligro y medidas se abren, editan y guardan en una sola escritura (FB-21, KAI-334). */
export type Riesgo = {peligro: PerfilLineaRead; medidas: PerfilLineaRead[]}

export type AperturaRiesgo = {modo: 'nuevo'} | {modo: 'editar'; riesgo: Riesgo}

/** Una medida con `clave` ya estaba guardada; sin clave es nueva. */
export type RiesgoEscrito = {clase: string; descripcion: string; medidas: {clave: string | null; texto: string}[]}

export function medidasDe(peligro: PerfilLineaRead, medidas: readonly PerfilLineaRead[]): PerfilLineaRead[] {
  return medidas.filter((medida) => medida.padre_clave !== null && medida.padre_clave === peligro.grupo_clave)
}

/** El servidor compara el texto exacto para marcar una línea «escrita a mano»: si solo cambiaron los
 * bordes, viaja el original y conserva su procedencia. */
function textoEscrito(original: string, escrito: string): string {
  return escrito.trim() === original.trim() ? original : escrito.trim()
}

/** Sin cambios devuelve el mismo borrador. Una medida nueva de un peligro guardado cuelga de
 * `campo:<id>`; si el peligro cambió, el servidor mueve las medidas a su clave nueva (KAI-242). */
export function borradorConRiesgo(
  guardadas: PerfilLineaWrite[],
  antes: Riesgo | null,
  escrito: RiesgoEscrito
): PerfilLineaWrite[] {
  const clase = (escrito.clase || null) as PerfilLineaWrite['clasificacion_gtc']
  const padre = antes ? antes.peligro.clave : claveDePeligro(escrito.clase, escrito.descripcion)
  const nuevas = escrito.medidas
    .filter((medida) => medida.clave === null && medida.texto.trim() !== '')
    .map((medida): PerfilLineaWrite => ({
      clave: null,
      bloque: 'medidas_prevencion',
      texto: medida.texto.trim(),
      confirmado: false,
      padre_clave: padre,
    }))
  if (!antes) {
    const peligro: PerfilLineaWrite = {
      clave: null,
      bloque: 'peligros',
      texto: escrito.descripcion.trim(),
      confirmado: false,
      clasificacion_gtc: clase,
    }
    return [...guardadas, peligro, ...nuevas]
  }
  const escritas = new Map(escrito.medidas.map((medida) => [medida.clave, medida.texto]))
  const suyas = new Set(antes.medidas.map((medida) => medida.clave))
  const lineas: PerfilLineaWrite[] = []
  let ancla = -1
  for (const linea of guardadas) {
    if (linea.clave === antes.peligro.clave) {
      lineas.push({...linea, texto: textoEscrito(linea.texto, escrito.descripcion), clasificacion_gtc: clase})
      ancla = lineas.length - 1
    } else if (linea.clave && suyas.has(linea.clave)) {
      const texto = escritas.get(linea.clave)
      if (texto === undefined) continue // quitada en el diálogo
      lineas.push({...linea, texto: textoEscrito(linea.texto, texto)})
      ancla = lineas.length - 1
    } else {
      lineas.push(linea)
    }
  }
  lineas.splice(ancla + 1, 0, ...nuevas)
  return lineas
}

/** Si otro peligro guardado comparte la clave (un duplicado viejo), sus medidas también son de ese y se quedan. */
export function medidasQueSeVan(riesgo: Riesgo, peligros: readonly PerfilLineaRead[]): PerfilLineaRead[] {
  const compartida = peligros.some(
    (otro) => otro.clave !== riesgo.peligro.clave && otro.grupo_clave === riesgo.peligro.grupo_clave
  )
  return compartida ? [] : riesgo.medidas
}

export function borradorSinRiesgo(
  guardadas: PerfilLineaWrite[],
  riesgo: Riesgo,
  peligros: readonly PerfilLineaRead[]
): PerfilLineaWrite[] {
  const salen = new Set([riesgo.peligro.clave, ...medidasQueSeVan(riesgo, peligros).map((medida) => medida.clave)])
  return guardadas.filter((linea) => !salen.has(linea.clave ?? ''))
}
